// Pre-export QA pass for every Headline / Description / Callout value.
// Returns null if the string should be REJECTED (blocklist match, empty,
// or unrecoverable). Otherwise returns the cleaned string, guaranteed to be
// <= maxLen chars AND to end on a complete word.

import { AD_ASSET_STOP_LABELS } from "./adAssetGuards.js";

// English word suffix patterns we accept as "complete word" endings.
// Longer patterns are checked first so "tion" beats "on".
const SAFE_WORD_SUFFIXES = [
    "tion", "sion", "ment", "ness", "ings", "able", "ible",
    "ing", "ers", "ors", "ies", "ish", "ist", "est", "ous",
    "ful", "ise", "ize", "ard", "ong", "ang", "ung",
    "ed", "ly", "es", "al", "as", "us", "is",
    "an", "on", "in", "en", "un",
    "ar", "or", "er", "ur", "ir",
    "s", "d", "g", "n", "r", "l", "m", "t", "k", "x", "z", "p", "b",
];

// Only *these* trailing characters trigger the mid-word heuristic — they're
// almost never legitimate word endings in Google Ads copy in English. Everything
// else is considered fine and left alone.
const SUSPICIOUS_TAIL_CHARS = new Set(["c", "v", "w", "q", "j", "f", "h"]);

const endsWithSuspiciousChar = (word) => {
    const lower = word.toLowerCase();
    // Explicit truncation markers.
    if (/\/$/.test(word)) return true;
    if (/[-–—]$/.test(word)) return true;
    // Grab alphabetical tail only.
    const tail = (lower.match(/[a-z]+$/) || [""])[0];
    if (!tail) return false;
    // If tail matches any known safe suffix, we're done.
    for (const suf of SAFE_WORD_SUFFIXES) {
        if (tail.endsWith(suf)) return false;
    }
    // Otherwise flag if the final char is in the suspicious set.
    return SUSPICIOUS_TAIL_CHARS.has(tail.slice(-1));
};

const looksMidWord = (str) => {
    if (!str) return false;
    const trimmed = String(str).trim();
    if (!trimmed) return false;
    // Sentence-ending punctuation, quotes, or closing brackets → complete.
    if (/[.!?)"'\]>%]$/.test(trimmed)) return false;
    const lastWord = (trimmed.match(/\S+$/) || [""])[0];
    if (!lastWord) return false;
    // Very short tokens (≤3 chars) like "cab", "24/7", "no." — trust them.
    if (lastWord.length <= 3 && !/\/$/.test(lastWord) && !/[-–—]$/.test(lastWord)) return false;
    return endsWithSuspiciousChar(lastWord);
};

/**
 * Sanitize a candidate ad-asset string:
 *   - Trims whitespace.
 *   - Rejects empty and blocklist matches (case-insensitive, including known typos).
 *   - If length > maxLen OR string appears to end mid-word, walks back to the
 *     last space (word-safe recovery). Falls back to hard-cut if no usable space.
 *   - Returns null when the asset should be dropped entirely.
 */
export const sanitizeAdAsset = (raw, maxLen) => {
    let s = String(raw ?? "").trim();
    if (!s) return null;
    if (AD_ASSET_STOP_LABELS.has(s.toLowerCase())) return null;

    const needsRecut = s.length > maxLen || looksMidWord(s);
    if (needsRecut) {
        const cut = s.length > maxLen ? s.slice(0, maxLen) : s;
        const lastSpace = cut.lastIndexOf(" ");
        if (lastSpace >= Math.max(6, Math.floor(maxLen / 3))) {
            s = cut.slice(0, lastSpace).replace(/[\s\-–—,.:;/]+$/, "");
        } else if (s.length > maxLen) {
            s = cut.replace(/[\s\-–—,.:;/]+$/, "");
        }
    }

    s = s.trim();
    if (!s || s.length > maxLen) return null;
    if (AD_ASSET_STOP_LABELS.has(s.toLowerCase())) return null;
    return s;
};
